import type { FeedItem } from '@knocklabs/client';
import { FilterStatus, useKnockFeed } from '@knocklabs/react';
import type { NotificationPayload } from '@piccolohealth/pbs-common';
import { ChakraV3, NumberedBadge, ScrollArea, Spin } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaArchive, FaCheck, FaCog } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../context/OrganizationContext';
import { NotificationItem } from './NotificationItem';

interface Props {
  onClose: () => void;
}

export const NotificationCenterContents = (props: Props) => {
  const { onClose } = props;

  const navigate = useNavigate();
  const organization = useOrganization();

  const { useFeedStore, feedClient } = useKnockFeed();
  const feedStore = useFeedStore();
  const notifications = feedStore.items as FeedItem<NotificationPayload>[];
  const isLoading = feedStore.loading;
  const unseenCount = feedStore.metadata.unseen_count;
  const hasNextPage = feedStore.metadata.total_count > feedStore.items.length;

  React.useEffect(() => {
    // When the feed client changes, or the status changes issue a re-fetch
    feedClient.fetch({ status: FilterStatus.All });
  }, [feedClient]);

  const markAllNotificationsAsRead = React.useCallback(() => {
    return Promise.all([feedClient.markAllAsSeen(), feedClient.markAllAsRead()]);
  }, [feedClient]);

  const markAllNotificationsAsArchived = React.useCallback(() => {
    return feedClient.markAllAsArchived();
  }, [feedClient]);

  const fetchNextPage = React.useCallback(() => {
    return feedClient.fetchNextPage();
  }, [feedClient]);

  const navigateToNotificationPreferences = React.useCallback(() => {
    void navigate(`/organizations/${organization.id}/user/profile/notifications`);
    onClose();
  }, [navigate, organization.id, onClose]);

  const content = P.run(() => {
    if (isLoading) {
      return <Spin />;
    }

    if (P.isEmpty(notifications)) {
      return (
        <ChakraV3.Result
          h='full'
          status='success'
          title={`You're all caught up`}
          description={
            <ChakraV3.Text fontSize='sm' color='secondary'>
              You will be notified here for, page activity, requests, and invites.
            </ChakraV3.Text>
          }
        />
      );
    }

    return (
      <ChakraV3.Stack gap='1px' bg='gray.200'>
        {notifications.map((notification) => (
          <NotificationItem key={notification.id} notification={notification} onClose={onClose} />
        ))}
        {hasNextPage && (
          <ChakraV3.HStack justify='center' bg='white' px={6} py={2}>
            <ChakraV3.Button variant='plain' onClick={fetchNextPage}>
              Load more
            </ChakraV3.Button>
          </ChakraV3.HStack>
        )}
      </ChakraV3.Stack>
    );
  });

  return (
    <ChakraV3.Stack h='2xl' w='lg' bg='white' gap={0} shadow='2xl' rounded='xl' overflow='hidden'>
      <ChakraV3.HStack px={6} py={3} borderBottomWidth='1px'>
        <ChakraV3.HStack>
          <ChakraV3.Text fontSize='xl' fontWeight='bold'>
            Notifications
          </ChakraV3.Text>
          <NumberedBadge count={unseenCount} />
        </ChakraV3.HStack>
        <ChakraV3.Spacer />
        <ChakraV3.Tooltip
          content='Mark all notifications as read'
          contentProps={{ fontSize: 'sm' }}
        >
          <ChakraV3.IconButton
            aria-label='Mark all notifications as read'
            size='sm'
            variant='ghost'
            color='gray.500'
            onClick={markAllNotificationsAsRead}
            disabled={isLoading || P.isEmpty(notifications)}
          >
            <FaCheck />
          </ChakraV3.IconButton>
        </ChakraV3.Tooltip>
        <ChakraV3.Tooltip content='Archive all notifications' contentProps={{ fontSize: 'sm' }}>
          <ChakraV3.IconButton
            aria-label='Archive all notifications'
            size='sm'
            variant='ghost'
            color='gray.500'
            onClick={markAllNotificationsAsArchived}
            disabled={isLoading || P.isEmpty(notifications)}
          >
            <FaArchive />
          </ChakraV3.IconButton>
        </ChakraV3.Tooltip>
        <ChakraV3.Tooltip content='Notification preferences' contentProps={{ fontSize: 'sm' }}>
          <ChakraV3.IconButton
            aria-label='Notification preferences'
            size='sm'
            variant='ghost'
            color='gray.500'
            onClick={navigateToNotificationPreferences}
          >
            <FaCog />
          </ChakraV3.IconButton>
        </ChakraV3.Tooltip>
      </ChakraV3.HStack>
      <ScrollArea h='full'>{content}</ScrollArea>
    </ChakraV3.Stack>
  );
};
